import { assign, createMachine, send } from "xstate";
import { cancel } from "xstate/lib/actions";
import { client } from "../../../../../..";
import { getRecordingPresignedUrls } from "../../../../../Queries/queries";
import { getRealStartAndEnd, getSrc } from "./utils";
import {
  SubscribeToExtendedPage_extendedPage_rhese,
  SubscribeToExtendedPage_extendedPage_rhese_data_audioRecording,
} from "../../../../../../editor/Project/api/__generated__/SubscribeToExtendedPage";

const getNow = () => Date.now();

export type ResourcesToLoad = string[];

export type Files = Array<{
  filename: string;
  blob: Blob;
  url: string;
}>;

export type Context = {
  resourcesToLoad: ResourcesToLoad;
  loadedFiles: Files;
  fetchingErrorMessage: string;
  audioElement: null | HTMLAudioElement;
  rhesesWithRecording: SubscribeToExtendedPage_extendedPage_rhese[];
  currentRheseId: null | string;
  successfulyPlayedRhese: null | string;
};

export type Events =
  | {
      type: "Load Resources";
      audioRecordings: SubscribeToExtendedPage_extendedPage_rhese_data_audioRecording[];
      rhesesWithPos: SubscribeToExtendedPage_extendedPage_rhese[];
      audioElement: HTMLAudioElement;
    }
  | {
      type: "On Load More";
      audioRecordings: SubscribeToExtendedPage_extendedPage_rhese_data_audioRecording[];
      rhesesWithPos: SubscribeToExtendedPage_extendedPage_rhese[];
    }
  | { type: "Play" }
  | { type: "Play when ready" }
  | { type: "Play this rhese"; rheseId: string }
  | { type: "Handle Playback Rate Change" }
  | { type: "Pause" }
  | { type: "Reached End"; shouldPlayNext: boolean; currentRheseId: string }
  | { type: "Resume" }
  | { type: "Failure" }
  | { type: "Close" }
  | { type: "Reset"; callback?: () => void }
  | { type: "Check Cache" }
  | { type: "Update Cache" }
  | { type: "Retry" };

export type Services = {
  "Run fetch Assets": {
    data: { files: Files };
  };
  "Attach audio ready listener": {
    data: { latency: number };
  };
  "Call async play on audio element": {
    data: { latency: number };
  };
};

export const audioPlayerMachine =
  /** @xstate-layout N4IgpgJg5mDOIC5QEECuECWB7ABABQBsBDATzACcBiAYQK1jAG0AGAXUVAAd6MAXbAHYcQAD0QBOAKwBmAHTiAjJIUB2AGwKATNPHNpAFgA0IEogAczWczXNmK8fZtr9Z-QF83xtJlyFSFSgAlODBeFnYkEG5YPkFhMQRNJVllZRVJTTUVTPE1aWNTBH1LZikFVX0FcXEzcUr3TxBvbHxiMioAeQEcABksIggcAFksciY2YWjYrCFIhI1ZBTVcqTUzbNqVfJNEAFpNZk1ZHUcNZn1JKX0Gr3QWv3bZAEkAOSeAFSfkHso+gZxgrAsKhyABjODhSY8fgzeJ7LRHOySSRqTSSdKlKQFRBohSyMzSaQo5jIsyXNFmDy3HytfzkWSBACiyAAIgBNSgPHC8AAWGFgOHIPJCkMiUxhs1ACXKzDx0gUZk0FmY63RmhU2IQ0iyi0qNUkthUemklMazV8bQoDOZ7NkAHVkB9XgBxTltUVcaFxOaIJTXfEo9ZqZHlaSaTSalFydHOMyK6pxzRUpp3C1062stmyPA9ZBsl2coioBgeqJe2E+xJKySyVEaRUWOpojU7BBbaN6Nb6bLdlUKZPm2mPJmZ7O5-MvV3BIig4WDRkCCCl8XeqW+mxmeThzT6VEqfSZRWaypyBSEpVrcr2VwD1NDq0j205vMFgASREXBDAQ4ARjOANYAkQvDftQPIfjAy7lpKoi+hY+iyPohL7j25RaGYkYGIhVxkr26gqCot40g8D42lmz4Ts69qOp8k44AAYh0gQ4CyyDvMglAQDMYCyBgAgAG5YP+PGDiR9KPuR44utRTp0YxzGsexCB8YJoLAYI4RQTEEpwggWiSJumg1GYqiKGSKiuJqCjWDWVRqPZBwIhoSZmneYkZk+UmTjJtHOgxTEsWxHEUOQoyyJwxC8AAZqMAC2siiZa4lkWOL7eQ6sl+fJgVKSpWBqRKmkTGK0G6QR4jHHGlTnCS0hbJImriDo8jla42g6LiRH3ElHmSWlVEZb5-nMcgACqLJPB0ODvFNADK7GBO8nHcbxAlCSJbk9RJqWUT5LrDTgY0TVNM04PNyCLcpa0FRpbBadMMHSnGeJVNutQuFIDWttZFnHCiSR1d2RpqF1abDilFHSYN+3ZUdk3TXNC1LSFYURcBMXkPFiXpttkPpTRMMBXDJ2Ixd7xXap6kzEVESetpq6wW2lSLFUrjrKGChIVZnNHDU1wHsoSjIi51LdTjENjbNjIskEcCoLF4y02W9MVmuVa-cswb2T9tVfYU1QqMc1g7lo4g7nGhGucRPWMoEgRMbLvDkCQ906ZWBKGzu5xZBYBGHBhrbdnI9hGhiJo7vZkig-e9I9B0rL7cgs1S+8s2UPRRAYAQIKK1CKuPYgYZqLWbWmVIBEKJqBKWNZwa1JzWRm-2Vti48ccJ3RScp2nGdZznjAKErK6q4zwZ4vuRfrNc1jOFX0g1zZ8pm5clQiym1vpu3E2d8njKp7IW8Fq7DMJHGiGSMU3bEuGFkRq2O6G2sdS5HoLjXlHLdg1ah8793B-x9vV0A8h6lUrFkI4S8zYP2RHkO+hR5TjwMvqBwFkUSW1Fl-WOADE6733j-IBmgQH510nVGsMgDiKE5pcBws9A7OFkOkBBQs7IXBuOvVu39sG-z3rNWQ9E97UFfAWLiAgeJ5WEglTam8uF+S7jwvhAihGTgpvlKmAgaZ5werpAksgkgKjqjULsTVIzZFrHo-Quh0jqAVNHdy+DDq4N4fw94giCwo3pGjaKcVJEbzbjIhxf9nGuOUXlG61M7rFTploysyI5BEmqHkOMuhcgtkKC4Wy6QyS1X0noDwjQBBYAgHAYQ2N2iaLdmrXYqgSjpH+uiA01Q9Z7G7Ihc4z10RGkbKaDBMdnhvE+N8cpJ89hmysAackBImr1TUFZZQ+IDCKgRAeWBbDSmkUzEMke0parHAVEqWwqpsipJxPYWseo8j2ByJzWxW0UrQ0nJsgueklBHBUFUEkBIdBEmuMeBwupNhmy2LuXIH8enuVxl5Z0jzdKhgqokm+Bp1h1CMK2J+iELLnAIkaUo6Qbni1HHjAaBM5IBUUsgaFlZyjrFrOHdUiL7A-NbIoBCDgLDhgbAcG8n9ekQv6jtRO41JoUrVqoHUeg3k-RkPPJUjVZQtQsKobItgNDSDxeDAlkK9okpGoKkmZ0kbCsZi84u9gqgyF0LubU2xCjWRkH9VESp+adlVdy8FEtRpSxZIahICL8RJANNcWJZ4q55DGSqOoqQGlrzWfSagcdPXet9EsBCFwdzFG1EsJwVcSg2DsA4F+sYXVgptnbJiiaECTJSA4dEKpzWc0jE1fEQMTTLCqNUcQarOEd1kY48tRJdFUoMWYIx88UXwIIromQpRjTqHDEoTtWDu0BPkfg8tZ4Krdm0NM2tMhjxbAYQ4JEehFS7mDAu-+S65H7yCUoqFJViGVkJCa2ougDLhikIca1iAa3HCBnYC4tg515LcEAA */
  createMachine(
    {
      tsTypes: {} as import("./audioPlayerMachine.typegen").Typegen0,
      id: "Audio Player",
      initial: "INITIAL",
      states: {
        INITIAL: {
          on: {
            "Load Resources": {
              target: "LOADING ASSETS",
              actions: "Set resources in context",
            },
          },
        },
        READY: {
          initial: "WAITING",
          states: {
            WAITING: {
              entry: ["Select rhese to play", "Play rhese if needed"],
              on: {
                Play: {
                  target: "PLAYING",
                },
              },
            },
            PLAYING: {
              entry: ["Play audio"],
              exit: ["Pause audio", "Cancel delay", "Rhese Played"],

              on: {
                Pause: {
                  target: "PAUSED",
                },
                "Reached End": {
                  target: "WAITING",
                },
                "Handle Playback Rate Change": {
                  target: "PLAYING",
                  internal: true,
                  actions: ["Cancel delay"],
                },
              },

              states: {
                "WAITING FOR DATA": {
                  invoke: {
                    src: "Attach audio ready listener",
                    onDone: "WAITING FOR AUDIO TO START",
                    onError: {
                      target: "#Audio Player.ERROR",
                    },
                  },
                },

                "PLAYING AUDIO": {
                  entry: ["Send 'Reached End' when done playing"],
                },

                "WAITING FOR AUDIO TO START": {
                  invoke: {
                    src: "Call async play on audio element",
                    onDone: "PLAYING AUDIO",
                    onError: {
                      target: "#Audio Player.ERROR",
                    },
                  },
                },
              },

              initial: "WAITING FOR DATA",
            },
            PAUSED: {
              on: {
                Resume: {
                  target: "PLAYING",
                },
              },
            },
          },
          on: {
            "Play this rhese": {
              target: "READY.WAITING",
              internal: false,
            },
          },
        },
        CLOSED: {},
        ERROR: {
          on: {
            Retry: "LOADING ASSETS",
          },
        },
        "LOADING ASSETS": {
          states: {
            LOADING: {
              always: [
                {
                  target: "#Audio Player.INITIAL",
                  cond: "There is no audio",
                },
                {
                  target: "#Audio Player.READY",
                  cond: "All files are loaded",
                },
                "FETCHING",
              ],
            },
            FETCHING: {
              invoke: {
                src: "Run fetch Assets",
                onDone: {
                  target: "LOADING",
                  actions: "Fetching Done",
                },
                onError: {
                  actions: "Fetching Error",
                  target: "FETCHING",
                  internal: true,
                },
              },
            },
          },
          initial: "LOADING",
          on: {
            Failure: [
              {
                cond: "Network Error",
                target: "LOADING ASSETS",
                internal: true,
              },
              "ERROR",
            ],
          },
        },
      },
      schema: {
        context: {} as Context,
        events: {} as Events,
        services: {} as Services,
      },
      context: {
        resourcesToLoad: [],
        loadedFiles: [],
        fetchingErrorMessage: "",
        audioElement: null,
        rhesesWithRecording: [],
        currentRheseId: null,
        successfulyPlayedRhese: null,
      },
      predictableActionArguments: true,
      preserveActionOrder: true,
      on: {
        Close: ".CLOSED",
        Reset: {
          target: ".INITIAL",
          actions: "Execute Reset Callback",
        },
        "On Load More": {
          // target: "LOADING ASSETS",
          actions: "Update resources in context",
        },
      },
    },
    {
      guards: {
        "All files are loaded": (context) => {
          const isFilenameInLoadedFile = (resourceName: string) =>
            context.loadedFiles.find((file) => file.filename === resourceName);
          return context.resourcesToLoad.every(isFilenameInLoadedFile);
        },
        "There is no audio": (context) => context.resourcesToLoad.length === 0,
        "Network Error": () => false,
      },
      actions: {
        "Select rhese to play": assign({
          successfulyPlayedRhese: (context, event) =>
            event.type === "Reached End" ? context.currentRheseId : null,
          currentRheseId: (context, event) => {
            if (event.type === "Reached End") {
              const nextRheseIndex =
                getOrderedRheses(context.rhesesWithRecording).findIndex(
                  (rhese) => rhese.data.id === context.currentRheseId,
                ) + 1;

              const nextRhese = context.rhesesWithRecording[nextRheseIndex];
              if (nextRhese) {
                return nextRhese.data.id;
              }
              return context.currentRheseId;
            } else if (event.type === "Play this rhese") {
              return event.rheseId;
            } else if (context.currentRheseId) {
              // Note: could happen after  an audio cropping, which will trigger a data change.
              // In this case we want to keep the same focused rhese
              return context.currentRheseId;
            } else {
              return getOrderedRheses(context.rhesesWithRecording)[0].data.id;
            }
          },
        }),
        "Set resources in context": assign((_context, event) => ({
          resourcesToLoad: [
            ...new Set(event.audioRecordings.map((recording) => recording.fileUuid)),
          ],
          rhesesWithRecording: event.rhesesWithPos,
          audioElement: event.audioElement,
        })),
        "Update resources in context": assign((_context, event) => ({
          rhesesWithRecording: event.rhesesWithPos,
        })),
        "Play rhese if needed": send((_context, event) => {
          const shouldPlay =
            (event.type === "Reached End" && event.shouldPlayNext) ||
            event.type === "Play this rhese";
          return {
            type: shouldPlay ? "Play" : "",
          };
        }),
        "Fetching Done": assign((context, event) => {
          return {
            loadedFiles: [...context.loadedFiles, ...event.data.files],
          };
        }),
        "Play audio": (context, event) => {
          const audioElement = context.audioElement;
          if (!audioElement) {
            throw new Error("'Play audio' triggered, but audioElement is not defined");
          }

          audioElement.muted = false;

          const audioRhese = context.rhesesWithRecording.find(
            (r) => r.data.id === context.currentRheseId,
          )!;
          const [realStart, realEnd] = getRealStartAndEnd(audioRhese.data.audioInfo);

          if (event.type === "Play") {
            if (!context.currentRheseId) {
              // Todo use https://xstate.js.org/docs/guides/typescript.html#typestates
              // But it is not supported in v4 but should be fixed in v5
              throw new Error("Should never be null");
            }

            // needed to reset playback rate, changing src will reset playbackRate info
            let playbackRate = audioElement.playbackRate;

            // "src" attribute
            let src = getSrc(
              context.currentRheseId,
              context.rhesesWithRecording,
              context.loadedFiles,
            );

            src += `#t=${realStart},${realEnd}`; // Note: perf optimization
            audioElement.src = src;
            audioElement.preload = "auto";
            audioElement.playbackRate = playbackRate;
            audioElement.dataset.rheseId = context.currentRheseId; // needed to be able to know when the audioElement is about a new rhese (Note: audio's src can remain the same for different rheses)
            audioElement.currentTime = realStart; // Begin at the right time
          }
        },
        "Send 'Reached End' when done playing": send(
          (context) => {
            const isLastRhese =
              getOrderedRheses(context.rhesesWithRecording)[context.rhesesWithRecording.length - 1]
                .data.id === context.currentRheseId;

            return {
              type: "Reached End",
              shouldPlayNext: !isLastRhese,
              currentRheseId: context.currentRheseId,
            };
          },
          {
            delay: (context) => {
              // We send Play message with a "end" with a delay to stop the audio at the right time
              if (!context.audioElement) {
                throw "'Select next rhese'>'delay' tiggered, but audioElement is not defined";
              }

              const audioRhese = context.rhesesWithRecording.find(
                (r) => r.data.id === context.currentRheseId,
              )!;
              let [realStart, realEnd] = getRealStartAndEnd(audioRhese.data.audioInfo);

              const playbackRate: number | undefined = context.audioElement.playbackRate;

              const currentTime = context.audioElement.currentTime;
              const diff = currentTime - realStart;
              const realStartWithOffset = realStart + diff;

              const durationInMs = (realEnd - realStartWithOffset) * 1000;
              return durationInMs * (1 / playbackRate);
            },
            id: "Delay as much as audio duration in ms", // give the event a unique ID
          },
        ),
        "Pause audio": (context) => {
          if (!context.audioElement) {
            throw new Error("Pause audio error");
          }
          context.audioElement.muted = true; // TODO Loic : keep or not ?
          context.audioElement.pause();
        },
        "Cancel delay": cancel("Delay as much as audio duration in ms"),
        "Fetching Error": assign({
          fetchingErrorMessage: (_context, event) => {
            let message = "An error occured";

            if (event.data instanceof Error) {
              message = event.data.message;
            }

            return message;
          },
        }),
        "Execute Reset Callback": (_context, event) => {
          event.callback?.();
        },
        "Rhese Played": () => {},
      },
      services: {
        "Run fetch Assets": async (context) => {
          const { resourcesToLoad, loadedFiles } = context;

          // Fetch only the new audios
          const filteredResourcesToLoad = resourcesToLoad.filter(
            (r) => !loadedFiles.find((f) => f.filename === r),
          );

          const jobs = filteredResourcesToLoad.map(async (filename) => {
            const { data } = await client.query({
              query: getRecordingPresignedUrls,
              variables: {
                filename,
              },
            });
            return {
              filename,
              getUrl: data?.audioRecording?.presignedUrl?.getUrl,
            };
          });

          const filenamesWithUrls = await Promise.all(jobs);

          const _jobs = filenamesWithUrls.map(async ({ filename, getUrl }) => {
            const data = await fetch(getUrl);
            const blob = await data.blob();

            return {
              filename,
              blob,
              url: URL.createObjectURL(blob),
            };
          });

          const files = await Promise.all(_jobs);

          return {
            files,
          };
        },
        "Attach audio ready listener": (context, event) =>
          new Promise((resolve, reject) => {
            const { audioElement } = context;

            if (!audioElement) {
              return reject(new Error("Attach audio ready listener doesn't have audioElement"));
            }

            if (event.type === "Resume" || event.type === "Handle Playback Rate Change") {
              return resolve({ latency: 0 });
            }

            const beforeStartDate = getNow();

            const MAX_TIMEOUT_IN_MS = 500;
            let alreadyCleared = false;
            const onLoadData = () => {
              if (audioElement.readyState >= 4) {
                const onReadyDate = getNow();
                const latency = onReadyDate - beforeStartDate;

                clearSideEffects();
                resolve({ latency });
              }
            };
            const onError = (e: any) => {
              clearSideEffects();
              return reject(new Error("Audio  addEventListener error", e));
            };

            const clearSideEffects = () => {
              if (alreadyCleared) return;
              alreadyCleared = true;
              clearTimeout(timeoutId);
              audioElement.removeEventListener("loadeddata", onLoadData);
              audioElement.removeEventListener("error", onError);
            };

            audioElement.addEventListener("loadeddata", onLoadData);
            audioElement.addEventListener("error", onError);

            const timeoutId = setTimeout(() => {
              const shouldResolve = !alreadyCleared;
              clearSideEffects();
              if (shouldResolve) {
                resolve({
                  latency: MAX_TIMEOUT_IN_MS,
                });
              }
            }, MAX_TIMEOUT_IN_MS);
          }),
        "Call async play on audio element": (context): Promise<{ latency: number }> => {
          return new Promise((resolve, reject) => {
            const start = performance.now();

            const { audioElement } = context;
            if (!(audioElement instanceof HTMLAudioElement)) {
              return reject("Audio element undefined");
            }

            audioElement.onplaying = () => {
              const end = performance.now();
              resolve({
                latency: end - start,
              });
              return;
            };

            audioElement.onerror = reject;

            if (audioElement.paused) {
              audioElement.play();
            }
          });
        },
      },
    },
  );

const getOrderedRheses = (rheses: SubscribeToExtendedPage_extendedPage_rhese[]) =>
  rheses.sort((a, b) => a.anchors[0].utf16Start - b.anchors[0].utf16Start);
